import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Edit, Plus, Trash2, CheckCircle, XCircle, Calendar, MoreVertical, Goal, Target, Flag, CheckSquare, Square, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext'; 
import AppLayout from './AppLayout';
import { useFirebase } from '../contexts/FirebaseContext';
import useLocalProgress from '../contexts/useLocalProgress';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColorScheme } from '../utils/colorSchemes';

const CareerProgress = ({ headshotUrl, uid, guest }) => {
  // Use userData from UserContext
  const { userData: contextUserData } = useUser();
  const localProgress = useLocalProgress(uid);
  const progressData = { ...localProgress.data, name: contextUserData.name, initials: String(contextUserData.name || '').split(' ').map(part => part[0]).join('').toUpperCase() };
  
  // Use toast hook
  const { showToast } = useToast();
  
  // ADD THEME CONTEXT
  const { theme, accentColor } = useTheme();
  
  // Calculate colors based on selected accent color
  const colors = useMemo(() => {
    const currentAccentColor = accentColor || 'maroon';
    return getColorScheme(currentAccentColor, theme);
  }, [accentColor, theme]);

  // Add this helper function here:
const getShadeBackground = (opacity = '15') => {
  return `${colors.primary}${opacity}`;
};
  
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentView, setCurrentView] = useState('Month');
  const [quickNote, setQuickNote] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskMenu, setShowTaskMenu] = useState(null);
  const [newTask, setNewTask] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [goalType, setGoalType] = useState("short-term");
  const [newGoal, setNewGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showGoalMenu, setShowGoalMenu] = useState(null);
  const [addingStep, setAddingStep] = useState(null);
  const [newDeadline, setNewDeadline] = useState(null); // New state for adding a deadline
  
  // New calendar enhancement state variables
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [currentWeekStartDate, setCurrentWeekStartDate] = useState(null);
  const [weekDays, setWeekDays] = useState([]);
  
  const navigate = useNavigate();

  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Day names for display
  const dayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  // Event types for classification and color coding
  const eventTypes = [
    { id: 'anatomy', label: 'Anatomy', color: 'anatomy' },
    { id: 'clinical', label: 'Clinical', color: 'clinical' },
    { id: 'exam', label: 'Exam/Test', color: 'exam' },
    { id: 'research', label: 'Research', color: 'research' },
    { id: 'meeting', label: 'Meeting', color: 'meeting' },
    { id: 'volunteering', label: 'Volunteering', color: 'volunteering' },
    { id: 'other', label: 'Other', color: 'other' }
  ];

  // Effect to update calendar when month/year changes
  useEffect(() => {
    // Generate calendar days for month view
    generateCalendarDays(currentMonth, currentYear);
    
    // Calculate the start date of the current week (Sunday)
    const firstOfMonth = new Date(currentYear, currentMonth, 1);
    const dayOfWeek = firstOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(firstOfMonth);
    startOfWeek.setDate(firstOfMonth.getDate() - dayOfWeek); // Go to the previous Sunday
    setCurrentWeekStartDate(startOfWeek);
  }, [currentMonth, currentYear]);
  
  // Effect to update week days when week start date changes
  useEffect(() => {
    if (currentWeekStartDate) {
      setWeekDays(generateWeekDays(currentWeekStartDate));
    }
  }, [currentWeekStartDate]);
  
  // Generate calendar days for current month
  const generateCalendarDays = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Calculate days from previous month to show
    const prevMonthDays = [];
    if (startingDayOfWeek > 0) {
      const prevMonth = new Date(year, month, 0);
      const prevMonthDaysCount = prevMonth.getDate();
      
      for (let i = prevMonthDaysCount - startingDayOfWeek + 1; i <= prevMonthDaysCount; i++) {
        prevMonthDays.push({
          day: i,
          month: month - 1,
          year: month === 0 ? year - 1 : year,
          isCurrentMonth: false
        });
      }
    }
    
    // Current month days
    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({
        day: i,
        month,
        year,
        isCurrentMonth: true
      });
    }
    
    // Calculate days from next month to show
    const nextMonthDays = [];
    const totalDaysShown = prevMonthDays.length + currentMonthDays.length;
    const remainingCells = 42 - totalDaysShown; // 6 rows x 7 days
    
    for (let i = 1; i <= remainingCells; i++) {
      nextMonthDays.push({
        day: i,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false
      });
    }
    
    setCalendarDays([...prevMonthDays, ...currentMonthDays, ...nextMonthDays]);
  };
  
  // Generate days for the week view
  const generateWeekDays = (weekStartDate) => {
    const weekDays = [];
    const startDate = new Date(weekStartDate);
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      weekDays.push({
        day: currentDate.getDate(),
        month: currentDate.getMonth(),
        year: currentDate.getFullYear(),
        isCurrentMonth: currentDate.getMonth() === currentMonth,
        date: currentDate
      });
    }
    
    return weekDays;
  };
  
  // Helper function to get events for a specific day (enhanced to return all events)
  const getEventsForDay = (day, month, year) => {
    return progressData.calendarEvents.filter(event => 
      event.day === day && event.month === month && event.year === year
    );
  };
  
  // Helper function to get event for a specific day (returns first event only)
  const getEventForDay = (day, month, year) => {
    return progressData.calendarEvents.find(event => 
      event.day === day && event.month === month && event.year === year
    );
  };

  // Format time in 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${formattedHour}:${minutes} ${period}`;
  };

  // Helper function to determine event background color based on event type or title
  const getEventColor = (event) => {
    // If event has an explicit type, use that
    if (event.eventType) {
      const eventTypeObj = eventTypes.find(type => type.id === event.eventType);
      if (eventTypeObj) {
        // Return dynamic colors based on event type
        switch (eventTypeObj.color) {
          case 'anatomy': return colors.primary;
          case 'clinical': return colors.accent;
          case 'exam': return colors.lightBg;
          case 'research': return '#E3B5A4';
          case 'meeting': return '#A65C5E';
          case 'volunteering': return '#C08552';
          default: return colors.primary;
        }
      }
    }
    
    // Fallback to scanning title for backward compatibility
    const title = typeof event === 'string' ? event : (event.title || '');
    
    if (title.includes("Anatomy")) {
      return colors.primary;
    } else if (title.includes("Clinical")) {
      return colors.accent;
    } else if (title.includes("Exam") || title.includes("Test")) {
      return colors.lightBg;
    } else if (title.includes("Research")) {
      return '#E3B5A4';
    } else if (title.includes("Meeting") || title.includes("Consultation")) {
      return '#A65C5E';
    } else if (title.includes("Volunteer")) {
      return '#C08552';
    }
    return colors.primary;
  };
  
  // A failed write must not change displayed data or produce a success toast.
  const saveUserData = localProgress.save;

  // Toggle card expansion
  const toggleCardExpansion = (cardName) => {
    setExpandedCard(expandedCard === cardName ? null : cardName);
  };
  
  // Previous month/week navigation
  const goToPreviousMonth = () => {
    if (currentView === 'Week') {
      goToPreviousWeek();
    } else {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    }
  };
  
  // Next month/week navigation
  const goToNextMonth = () => {
    if (currentView === 'Week') {
      goToNextWeek();
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };
  
  // Function to navigate to the previous week
  const goToPreviousWeek = () => {
    const prevWeek = new Date(currentWeekStartDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentWeekStartDate(prevWeek);
    
    // Update current month and year if they changed
    if (prevWeek.getMonth() !== currentMonth) {
      setCurrentMonth(prevWeek.getMonth());
      if (prevWeek.getFullYear() !== currentYear) {
        setCurrentYear(prevWeek.getFullYear());
      }
    }
  };
  
  // Function to navigate to the next week
  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStartDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentWeekStartDate(nextWeek);
    
    // Update current month and year if they changed
    if (nextWeek.getMonth() !== currentMonth) {
      setCurrentMonth(nextWeek.getMonth());
      if (nextWeek.getFullYear() !== currentYear) {
        setCurrentYear(nextWeek.getFullYear());
      }
    }
  };

// Handle view change
  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'Week' && !currentWeekStartDate) {
      // Initialize week view if needed
      const today = new Date();
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      setCurrentWeekStartDate(startOfWeek);
    }
  };
  
  // Save note function
  const saveNote = () => {
    if (!quickNote.trim()) return;
    
    const newNote = {
      id: Date.now(),
      content: quickNote,
      date: new Date().toISOString()
    };
    
    const updatedNotes = [...(progressData.notes || []), newNote];
    if (!saveUserData({ notes: updatedNotes })) return;
    setQuickNote('');
    showToast('Note saved successfully!', 'success');
  };
  
  // Show deadline edit form
  const handleEditDeadline = (deadline) => {
    setEditingDeadline({ ...deadline });
  };
  
  // Save deadline changes
  const saveDeadlineChanges = () => {
    if (!editingDeadline) return;
    
    const updatedDeadlines = progressData.deadlines.map(deadline => 
      deadline.id === editingDeadline.id ? editingDeadline : deadline
    );
    
    if (!saveUserData({ deadlines: updatedDeadlines })) return;
    setEditingDeadline(null);
    showToast('Deadline updated successfully!', 'success');
  };
  
  // Cancel deadline editing
  const cancelDeadlineEdit = () => {
    setEditingDeadline(null);
  };

  // Start adding new deadline
const startAddDeadline = () => {
  setNewDeadline({
    title: '',
    type: 'Assignment',
    date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  });
};

// Save new deadline
const saveNewDeadline = () => {
  if (!newDeadline || !newDeadline.title.trim()) return;
  
  const deadline = {
    id: Date.now(),
    title: newDeadline.title,
    type: newDeadline.type,
    date: newDeadline.date
  };
  
  const updatedDeadlines = [...progressData.deadlines, deadline];
  if (!saveUserData({ deadlines: updatedDeadlines })) return;
  setNewDeadline(null);
  showToast('New deadline added!', 'success');
};

// Cancel adding new deadline
const cancelAddDeadline = () => {
  setNewDeadline(null);
};
  
  // Toggle task menu
  const toggleTaskMenu = (taskId) => {
    setShowTaskMenu(showTaskMenu === taskId ? null : taskId);
  };
  
  // Edit task
  const handleEditTask = (task) => {
    setEditingTask({ ...task });
    setShowTaskMenu(null);
  };
  
  // Save task changes
  const saveTaskChanges = () => {
    if (!editingTask) return;
    
    const updatedTasks = progressData.tasks.map(task => 
      task.id === editingTask.id ? editingTask : task
    );
    
    if (!saveUserData({ tasks: updatedTasks })) return;
    setEditingTask(null);
    showToast('Task updated successfully!', 'success');
  };
  
  // Cancel task editing
  const cancelTaskEdit = () => {
    setEditingTask(null);
  };
  
  // Delete task
  const deleteTask = (taskId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this task?');
    if (!confirmDelete) return;
    
    const updatedTasks = progressData.tasks.filter(task => task.id !== taskId);
    if (!saveUserData({ tasks: updatedTasks })) return;
    setShowTaskMenu(null);
    showToast('Task deleted', 'info');
  };
  
  // Change task status
  const changeTaskStatus = (taskId, newStatus) => {
    const updatedTasks = progressData.tasks.map(task => 
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    
    if (!saveUserData({ tasks: updatedTasks })) return;
    setShowTaskMenu(null);
    
    // Show appropriate message based on the new status
    if (newStatus === 'completed') {
      showToast('Task marked as completed!', 'success');
    } else {
      showToast(`Task moved to ${newStatus === 'todo' ? 'To Do' : 'In Progress'}`, 'info');
    }
  };
  
  // Start adding new task
  const startAddTask = (status) => {
    setNewTask({
      title: '',
      dueDate: new Date().toISOString().split('T')[0],
      status
    });
  };
  
  // Save new task
  const saveNewTask = () => {
    if (!newTask || !newTask.title.trim()) return;
    
    const task = {
      id: Date.now(),
      title: newTask.title,
      dueDate: newTask.dueDate,
      status: newTask.status
    };
    
    const updatedTasks = [...progressData.tasks, task];
    if (!saveUserData({ tasks: updatedTasks })) return;
    setNewTask(null);
    showToast('New task created!', 'success');
  };
  
  // Cancel adding new task
  const cancelAddTask = () => {
    setNewTask(null);
  };

  // Update handleAddEvent function to open the event creation modal with event type
  const handleAddEvent = (dayInfo) => {
    setSelectedDay(dayInfo);
    setNewEvent({
      id: null,
      title: '',
      eventType: 'other', // Default event type
      description: '',
      startTime: '09:00',
      endTime: '10:00',
      day: dayInfo.day,
      month: dayInfo.month,
      year: dayInfo.year
    });
    setShowEventModal(true);
  };
  
  // Function to save the new event
  const saveNewEvent = () => {
    if (!newEvent || !newEvent.title.trim()) {
      showToast('Please enter an event title', 'error');
      return;
    }
    
    let updatedEvents;
    
    if (newEvent.id) {
      // Edit existing event
      updatedEvents = progressData.calendarEvents.map(event => 
        event.id === newEvent.id ? newEvent : event
      );

    } else {
      // Add new event
      const eventWithId = {
        ...newEvent,
        id: Date.now()
      };
      updatedEvents = [...progressData.calendarEvents, eventWithId];

    }
    
    if (!saveUserData({ calendarEvents: updatedEvents })) return;
    showToast('Calendar saved in this browser', 'success');
    setShowEventModal(false);
    setNewEvent(null);
  };
  
  // Function to cancel adding a new event
  const cancelAddEvent = () => {
    setShowEventModal(false);
    setNewEvent(null);
  };

  // Edit an existing event
  const handleEditEvent = (event) => {
    setSelectedDay({
      day: event.day,
      month: event.month,
      year: event.year
    });
    setNewEvent({...event});
    setShowEventModal(true);
  };
  
  // Delete an event
  const handleDeleteEvent = (eventId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this event?');
    if (!confirmDelete) return;
    
    const updatedEvents = progressData.calendarEvents.filter(event => event.id !== eventId);
    if (!saveUserData({ calendarEvents: updatedEvents })) return;
    showToast('Calendar saved in this browser', 'success');
    setShowEventModal(false);
    setNewEvent(null);
    showToast('Event deleted', 'info');
  };

  // Calculate progress percentages
  const calculatePercentage = (current, total) => {
    return total > 0 ? (current / total) * 100 : 0;
  };

  const coursePercentage = calculatePercentage(progressData.completedCourses, progressData.totalCourses);
  const clinicalPercentage = calculatePercentage(progressData.clinicalHours, progressData.requiredClinicalHours);
  const researchPercentage = calculatePercentage(progressData.researchProjects, progressData.requiredResearchProjects);

  // Find tasks by status
  const getTasksByStatus = (status) => {
    return progressData.tasks.filter(task => task.status === status);
  };
  // Goals management functions
  const toggleGoalType = (type) => {
    setGoalType(type);
  };
  
  const startAddGoal = () => {
    setNewGoal({
      title: '',
      deadline: new Date().toISOString().split('T')[0],
      isCompleted: false,
      steps: []
    });
  };
  
  const cancelAddGoal = () => {
    setNewGoal(null);
  };
  
  const saveNewGoal = () => {
    if (!newGoal || !newGoal.title.trim()) return;
    
    const goal = {
      id: Date.now(),
      title: newGoal.title,
      deadline: newGoal.deadline,
      isCompleted: false,
      createdAt: new Date().toISOString().split('T')[0],
      steps: newGoal.steps || []
    };
    
    if (goalType === "short-term") {
      const updatedGoals = [...progressData.shortTermGoals, goal];
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
      showToast('New short-term goal created!', 'success');
    } else {
      const updatedGoals = [...progressData.longTermGoals, goal];
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
      showToast('New long-term goal created!', 'success');
    }
    
    setNewGoal(null);
  };
  
  const toggleGoalMenu = (goalId) => {
    setShowGoalMenu(showGoalMenu === goalId ? null : goalId);
  };
  
  const handleEditGoal = (goal) => {
    setEditingGoal({ ...goal });
    setShowGoalMenu(null);
  };
  
  const saveGoalChanges = () => {
    if (!editingGoal) return;
    
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.map(goal => 
        goal.id === editingGoal.id ? editingGoal : goal
      );
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
      showToast('Short-term goal updated!', 'success');
    } else {
      const updatedGoals = progressData.longTermGoals.map(goal => 
        goal.id === editingGoal.id ? editingGoal : goal
      );
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
      showToast('Long-term goal updated!', 'success');
    }
    
    setEditingGoal(null);
  };
  
  const cancelEditGoal = () => {
    setEditingGoal(null);
  };
  
  const deleteGoal = (goalId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this goal?');
    if (!confirmDelete) return;
    
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.filter(goal => goal.id !== goalId);
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
      showToast('Short-term goal deleted', 'info');
    } else {
      const updatedGoals = progressData.longTermGoals.filter(goal => goal.id !== goalId);
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
      showToast('Long-term goal deleted', 'info');
    }
    
    setShowGoalMenu(null);
  };
  
  const toggleGoalCompletion = (goalId) => {
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.map(goal => {
        if (goal.id === goalId) {
          const newCompletionState = !goal.isCompleted;
          return { ...goal, isCompleted: newCompletionState };
        }
        return goal;
      });
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
    } else {
      const updatedGoals = progressData.longTermGoals.map(goal => {
        if (goal.id === goalId) {
          const newCompletionState = !goal.isCompleted;
          return { ...goal, isCompleted: newCompletionState };
        }
        return goal;
      });
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
    }
  };
  
  const addStepToGoal = (goalId) => {
    setAddingStep(goalId);
  };
  
  const saveNewStep = (goalId, stepTitle) => {
    if (!stepTitle.trim()) return;
    
    const newStep = {
      id: Date.now(),
      title: stepTitle,
      isCompleted: false
    };
    
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.map(goal => {
        if (goal.id === goalId) {
          return {
            ...goal,
            steps: [...(goal.steps || []), newStep]
          };
        }
        return goal;
      });
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
    } else {
      const updatedGoals = progressData.longTermGoals.map(goal => {
        if (goal.id === goalId) {
          return {
            ...goal,
            steps: [...(goal.steps || []), newStep]
          };
        }
        return goal;
      });
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
    }
    
    setAddingStep(null);
    showToast('New step added to goal', 'success');
  };
  
  const toggleStepCompletion = (goalId, stepId) => {
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.map(goal => {
        if (goal.id === goalId && goal.steps) {
          return {
            ...goal,
            steps: goal.steps.map(step => {
              if (step.id === stepId) {
                const newCompletionState = !step.isCompleted;
                return { ...step, isCompleted: newCompletionState };
              }
              return step;
            })
          };
        }
        return goal;
      });
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
    } else {
      const updatedGoals = progressData.longTermGoals.map(goal => {
        if (goal.id === goalId && goal.steps) {
          return {
            ...goal,
            steps: goal.steps.map(step => {
              if (step.id === stepId) {
                const newCompletionState = !step.isCompleted;
                return { ...step, isCompleted: newCompletionState };
              }
              return step;
            })
          };
        }
        return goal;
      });
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
    }
  };
  
  const deleteStep = (goalId, stepId) => {
    if (goalType === "short-term") {
      const updatedGoals = progressData.shortTermGoals.map(goal => {
        if (goal.id === goalId && goal.steps) {
          return {
            ...goal,
            steps: goal.steps.filter(step => step.id !== stepId)
          };
        }
        return goal;
      });
      if (!saveUserData({ shortTermGoals: updatedGoals })) return;
    } else {
      const updatedGoals = progressData.longTermGoals.map(goal => {
        if (goal.id === goalId && goal.steps) {
          return {
            ...goal,
            steps: goal.steps.filter(step => step.id !== stepId)
          };
        }
        return goal;
      });
      if (!saveUserData({ longTermGoals: updatedGoals })) return;
    }
    showToast('Step removed from goal', 'info');
  };
  
  // Calculate goal completion percentage
  const calculateGoalProgress = (goal) => {
    if (!goal.steps || goal.steps.length === 0) {
      return goal.isCompleted ? 100 : 0;
    }
    
    const completedSteps = goal.steps.filter(step => step.isCompleted).length;
    return Math.round((completedSteps / goal.steps.length) * 100);
  };

  // Handle calendar event click
  const handleEventClick = (event) => {
    handleEditEvent(event);
  };

  // Render the month view calendar with better-fitting events
  const renderMonthCalendar = () => (
    <div 
      className="grid grid-cols-7 gap-px rounded-lg overflow-hidden"
      style={{ backgroundColor: colors.primary + '50' }}
    >
      {/* Calendar Headers */}
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <div 
          key={day}
          className="p-2 text-center text-white text-sm"
          style={{ backgroundColor: colors.primary + '70' }}
        >
          {day}
        </div>
      ))}
      
      {/* Calendar Days */}
      {calendarDays.map((dayInfo, index) => {
        const events = getEventsForDay(dayInfo.day, dayInfo.month, dayInfo.year);
        const isToday = new Date().getDate() === dayInfo.day && 
                        new Date().getMonth() === dayInfo.month && 
                        new Date().getFullYear() === dayInfo.year;
        
        return (
          <div 
            key={index} 
            className={`${dayInfo.isCurrentMonth ? 'bg-white' : ''} 
                      ${isToday ? 'ring-2' : ''} 
                      h-20 p-1 relative cursor-pointer hover:bg-gray-50 group transition-colors duration-150`}
            style={{
              backgroundColor: dayInfo.isCurrentMonth ? colors.cardBg : colors.lightBg + '50',
              ringColor: isToday ? colors.primary : 'transparent',
              color: colors.text
            }}
            onClick={() => dayInfo.isCurrentMonth && handleAddEvent(dayInfo)}
          >
            <span className={`text-xs ${!dayInfo.isCurrentMonth ? 'text-gray-500' : ''}`}>
              {dayInfo.day}
            </span>
            
            {/* Events container with scrolling */}
            <div className="mt-1 max-h-14 overflow-y-auto">
              {events.map((event, eventIndex) => (
                <div 
                  key={eventIndex}
                  className="mb-0.5 text-xs py-0.5 px-1 text-white rounded cursor-pointer hover:bg-opacity-90 truncate"
                  style={{ backgroundColor: getEventColor(event) }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the parent div's onClick
                    handleEventClick(event);
                  }}
                >
                  {event.title}
                </div>
              ))}
              
              {events.length > 2 && (
                <div className="text-xs text-gray-600 pl-1 mt-0.5">
                  +{events.length - 2} more
                </div>
              )}
            </div>
            
            {/* Add event button (only for current month) */}
            {dayInfo.isCurrentMonth && (
              <button 
                className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ color: colors.primary }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent div's onClick
                  handleAddEvent(dayInfo);
                }}
              >
                <Plus className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
  
  // Render the week view calendar with color-coded events
  const renderWeekCalendar = () => {
    if (!weekDays.length) return null;
    
    return (
      <div 
        className="flex flex-col rounded-lg overflow-hidden"
        style={{ backgroundColor: colors.primary + '20' }}
      >
        {/* Each day becomes a row */}
        {weekDays.map((day, index) => {
          const events = getEventsForDay(day.day, day.month, day.year);
          const isToday = new Date().getDate() === day.day && 
                        new Date().getMonth() === day.month && 
                        new Date().getFullYear() === day.year;
          
          return (
            <div key={index} className="mb-0.5">
              {/* Day header - more compact */}
              <div 
                className="p-1.5 flex justify-between items-center"
                style={{ 
                  backgroundColor: isToday ? colors.primary + '70' : colors.primary + '40'
                }}
              >
                <div className="flex items-center">
                  <div className="text-lg font-medium text-white mr-2">{day.day}</div>
                  <div className="text-xs">
                    <div className="text-white">{dayNames[index]}</div>
                    <div className="text-gray-300">{monthNames[day.month]}</div>
                  </div>
                </div>
                
                {/* Add event button */}
                <button 
                  className="hover:opacity-80"
                  style={{ color: colors.primary }}
                  onClick={() => handleAddEvent(day)}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              
              {/* Events container - reduced height */}
              <div 
                className="p-1.5 min-h-8"
                style={{ backgroundColor: colors.cardBg }}
              >
                {events.length === 0 ? (
                  <div className="text-gray-500 text-xs italic px-2">No events</div>
                ) : (
                  <div className="space-y-1">
                    {events.map((event, eventIndex) => (
                      <div 
                        key={eventIndex}
                        className="p-1.5 rounded text-white cursor-pointer hover:bg-opacity-90 text-xs"
                        style={{ backgroundColor: getEventColor(event) }}
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="font-medium">{event.title}</div>
                        {event.startTime && event.endTime && (
                          <div className="text-xs text-white text-opacity-90">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </div>
                        )}
                        {/* Only show description if there's only one event */}
                        {events.length === 1 && event.description && (
                          <div className="text-xs mt-0.5 text-white text-opacity-90 truncate">{event.description}</div>
                        )}
                      </div>
                    ))}
                    
                    {/* Only show "more" indicator if we have multiple events */}
                    {events.length > 2 && (
                      <div className="text-xs text-right text-gray-400 pr-1">
                        {events.length > 2 && `+${events.length - 2} more`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render all progress content
  const renderContent = () => (
    <div style={{ backgroundColor: colors.background }}>
      {/* Progress Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Course Complete Card */}
        <div className={`bg-white rounded-lg shadow-md p-4 ${expandedCard === 'courses' ? 'row-span-2' : ''}`}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium" style={{ color: colors.text }}>Courses Complete</h3>
            <button 
              className="text-gray-500 hover:opacity-80"
              style={{ color: colors.primary }}
              onClick={() => toggleCardExpansion('courses')}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              </svg>
            </button>
          </div>
          <div className="text-center py-2">
            <p className="text-4xl font-bold" style={{ color: colors.primary }}>Not connected</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="h-2 rounded-full" 
                style={{ 
                  width: `${coursePercentage}%`,
                  backgroundColor: colors.primary
                }}
              ></div>
            </div>
          </div>
          
          {expandedCard === 'courses' && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h4 className="font-medium mb-2" style={{ color: colors.text }}>Course Progress Details</h4>
              <ul className="space-y-2">
                <li className="flex justify-between text-gray-700">
                  <span>Required Courses</span>
                  <span>{progressData.totalCourses}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Completed</span>
                  <span>{progressData.completedCourses}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Remaining</span>
                  <span>{progressData.totalCourses - progressData.completedCourses}</span>
                </li>
                <li className="flex justify-between font-medium" style={{ color: colors.primary }}>
                  <span>Completion Rate</span>
                  <span>{coursePercentage.toFixed(1)}%</span>
                </li>
              </ul>
              <button 
                className="w-full mt-4 text-white py-2 rounded-lg transition-colors duration-300"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                onClick={() => navigate('/app/profile?section=courses')}
              >
                View All Courses
              </button>
            </div>
          )}
        </div>

        {/* Clinical Hours Card */}
        <div className={`bg-white rounded-lg shadow-md p-4 ${expandedCard === 'clinical' ? 'row-span-2' : ''}`}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium" style={{ color: colors.text }}>Clinical Hours</h3>
            <button 
              className="text-gray-500 hover:opacity-80"
              style={{ color: colors.primary }}
              onClick={() => toggleCardExpansion('clinical')}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              </svg>
            </button>
          </div>
          <div className="text-center py-2">
            <p className="text-4xl font-bold" style={{ color: colors.primary }}>Not connected</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="h-2 rounded-full" 
                style={{ 
                  width: `${clinicalPercentage}%`,
                  backgroundColor: colors.primary
                }}
              ></div>
            </div>
          </div>
          
          {expandedCard === 'clinical' && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h4 className="font-medium mb-2" style={{ color: colors.text }}>Clinical Hours Details</h4>
              <ul className="space-y-2">
                <li className="flex justify-between text-gray-700">
                  <span>Required Hours</span>
                  <span>{progressData.requiredClinicalHours}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Completed</span>
                  <span>{progressData.clinicalHours}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Remaining</span>
                  <span>{progressData.requiredClinicalHours - progressData.clinicalHours}</span>
                </li>
                <li className="flex justify-between font-medium" style={{ color: colors.primary }}>
                  <span>Completion Rate</span>
                  <span>{clinicalPercentage.toFixed(1)}%</span>
                </li>
              </ul>
              <button 
                className="w-full mt-4 text-white py-2 rounded-lg transition-colors duration-300"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                onClick={() => navigate('/app/clinical')}
              >
                View Clinical Details
              </button>
            </div>
          )}
        </div>

        {/* Research Projects Card */}
        <div className={`bg-white rounded-lg shadow-md p-4 ${expandedCard === 'research' ? 'row-span-2' : ''}`}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium" style={{ color: colors.text }}>Research Projects</h3>
            <button 
              className="text-gray-500 hover:opacity-80"
              style={{ color: colors.primary }}
              onClick={() => toggleCardExpansion('research')}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              </svg>
            </button>
          </div>
          <div className="text-center py-2">
            <p className="text-4xl font-bold" style={{ color: colors.primary }}>Not connected</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className="h-2 rounded-full" 
                style={{ 
                  width: `${researchPercentage}%`,
                  backgroundColor: colors.primary
                }}
              ></div>
            </div>
          </div>
          
          {expandedCard === 'research' && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h4 className="font-medium mb-2" style={{ color: colors.text }}>Research Projects Details</h4>
              <ul className="space-y-2">
                <li className="flex justify-between text-gray-700">
                  <span>Required Projects</span>
                  <span>{progressData.requiredResearchProjects}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Completed</span>
                  <span>{progressData.researchProjects}</span>
                </li>
                <li className="flex justify-between text-gray-700">
                  <span>Remaining</span>
                  <span>{progressData.requiredResearchProjects - progressData.researchProjects}</span>
                </li>
                <li className="flex justify-between font-medium" style={{ color: colors.primary }}>
                  <span>Completion Rate</span>
                  <span>{researchPercentage.toFixed(1)}%</span>
                </li>
              </ul>
              <button 
                className="w-full mt-4 text-white py-2 rounded-lg transition-colors duration-300"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                onClick={() => navigate('/app/research')}
              >
                View Research Projects
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Academic Calendar Section */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-semibold" style={{ color: colors.text }}>Academic Calendar</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Schedule and track your academic activities</p>
            
            <div className="flex justify-between mb-4">
              <div className="flex space-x-2">
                <div className="relative">
                  <select 
                    className="py-1 px-4 pr-8 rounded-full appearance-none border"
                    style={{
                      backgroundColor: colors.lightBg,
                      color: colors.text,
                      borderColor: colors.accent
                    }}
                    value={currentMonth}
                    onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>{month}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2" style={{ color: colors.text }}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>
                
                <div className="relative">
                  <select 
                    className="py-1 px-4 pr-8 rounded-full appearance-none border"
                    style={{
                      backgroundColor: colors.lightBg,
                      color: colors.text,
                      borderColor: colors.accent
                    }}
                    value={currentYear}
                    onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                  >
                    {/* Generate range of years (5 years before to 10 years after current year) */}
                    {Array.from({ length: 16 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2" style={{ color: colors.text }}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>
              </div>
              
              <div className="flex">
                <div className="relative">
                  <select 
                    className="py-1 px-4 pr-8 rounded-full appearance-none border"
                    style={{
                      backgroundColor: colors.lightBg,
                      color: colors.text,
                      borderColor: colors.accent
                    }}
                    value={currentView}
                    onChange={(e) => handleViewChange(e.target.value)}
                  >
                    <option>Month</option>
                    <option>Week</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2" style={{ color: colors.text }}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>
                
                <button 
                  className="py-1 px-3 rounded-full ml-1 border"
                  style={{
                    backgroundColor: colors.lightBg,
                    color: colors.text,
                    borderColor: colors.accent
                  }}
                  onClick={goToPreviousMonth}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                <button 
                  className="py-1 px-3 rounded-full ml-1 border"
                  style={{
                    backgroundColor: colors.lightBg,
                    color: colors.text,
                    borderColor: colors.accent
                  }}
                  onClick={goToNextMonth}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Calendar View (Month or Week) */}
            {currentView === 'Month' ? renderMonthCalendar() : renderWeekCalendar()}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Upcoming Deadlines Section */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold" style={{ color: colors.text }}>Upcoming Deadlines</h3>
              <button 
                className="hover:opacity-80"
                style={{ color: colors.primary }}
                onClick={startAddDeadline}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            {newDeadline ? (
              <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: getShadeBackground('20') }}>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newDeadline.title}
                    onChange={(e) => setNewDeadline({...newDeadline, title: e.target.value})}
                    className="w-full bg-white border rounded p-2"
                    style={{ 
                      borderColor: colors.accent,
                      color: colors.text
                    }}
                    placeholder="Deadline title"
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <select
                      value={newDeadline.type}
                      onChange={(e) => setNewDeadline({...newDeadline, type: e.target.value})}
                      className="bg-white border rounded p-2 flex-1"
                      style={{ 
                        borderColor: colors.accent,
                        color: colors.text
                      }}
                    >
                      <option value="Assignment">Assignment</option>
                      <option value="Practical">Practical</option>
                      <option value="Exam">Exam</option>
                      <option value="Project">Project</option>
                    </select>
                    <input
                      type="text"
                      value={newDeadline.date}
                      onChange={(e) => setNewDeadline({...newDeadline, date: e.target.value})}
                      className="bg-white border rounded p-2 flex-1"
                      style={{ 
                        borderColor: colors.accent,
                        color: colors.text
                      }}
                      placeholder="Date (e.g., Feb 28, 2025)"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 mt-2">
                    <button
                      onClick={cancelAddDeadline}
                      className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveNewDeadline}
                      className="px-3 py-1 text-white rounded"
                      style={{ backgroundColor: colors.primary }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                      onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                      disabled={!newDeadline.title.trim()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            
            <div className="space-y-3">
              {editingDeadline ? (
                <div className="p-3 rounded-lg" style={{ backgroundColor: getShadeBackground('20') }}>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingDeadline.title}
                      onChange={(e) => setEditingDeadline({...editingDeadline, title: e.target.value})}
                      className="w-full bg-white border rounded p-2"
                      style={{ 
                        borderColor: colors.accent,
                        color: colors.text
                      }}
                      placeholder="Title"
                    />
                    <div className="flex space-x-2">
                      <select
                        value={editingDeadline.type}
                        onChange={(e) => setEditingDeadline({...editingDeadline, type: e.target.value})}
                        className="bg-white border rounded p-2 flex-1"
                        style={{ 
                          borderColor: colors.accent,
                          color: colors.text
                        }}
                      >
                        <option value="Assignment">Assignment</option>
                        <option value="Practical">Practical</option>
                        <option value="Exam">Exam</option>
                        <option value="Project">Project</option>
                      </select>
                      <input
                        type="text"
                        value={editingDeadline.date}
                        onChange={(e) => setEditingDeadline({...editingDeadline, date: e.target.value})}
                        className="bg-white border rounded p-2 flex-1"
                        style={{ 
                          borderColor: colors.accent,
                          color: colors.text
                        }}
                        placeholder="Date (e.g., Feb 28, 2025)"
                      />
                    </div>
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={cancelDeadlineEdit}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveDeadlineChanges}
                        className="px-3 py-1 text-white rounded"
                        style={{ backgroundColor: colors.primary }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                progressData.deadlines.map(deadline => (
                  <div key={deadline.id} className="p-3 rounded-lg" style={{ backgroundColor: getShadeBackground('20') }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium" style={{ color: colors.text }}>{deadline.title}</h4>
                        <p className="text-sm text-gray-600">{deadline.type} • {deadline.date}</p>
                      </div>
                      <button 
                        className="text-gray-500 hover:opacity-80"
                        style={{ color: colors.primary }}
                        onClick={() => handleEditDeadline(deadline)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
              
              {progressData.deadlines.length === 0 && !newDeadline && (
                <div className="text-center py-4 text-gray-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No upcoming deadlines</p>
                  <button
                    className="mt-2 text-sm"
                    style={{ color: colors.primary }}
                    onClick={startAddDeadline}
                  >
                    + Add your first deadline
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Quick Notes Section */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold" style={{ color: colors.text }}>Quick Notes</h3>
            </div>
            
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: getShadeBackground('20') }}>
              <textarea 
                placeholder="Add a quick note about your studies..." 
                className="w-full bg-transparent border-none placeholder-gray-500 focus:outline-none resize-none"
                style={{ color: colors.text }}
                value={quickNote}
                onChange={(e) => setQuickNote(e.target.value)}
                rows={3}
              ></textarea>
            </div>
            
            <button 
              className="w-full text-white py-2 rounded-lg transition-colors duration-300"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
              onClick={saveNote}
              disabled={!quickNote.trim()}
            >
              Save Note
            </button>
            
            {/* Display recent notes */}
            {progressData.notes && progressData.notes.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Recent Notes</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {progressData.notes.slice().reverse().map(note => (
                    <div key={note.id} className="p-2 rounded text-sm" style={{ backgroundColor: getShadeBackground('20') }}>
                      <p style={{ color: colors.text }}>{note.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(note.date).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Student Goals Section */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xl font-semibold" style={{ color: colors.text }}>My Goals</h3>
          <button 
            className="flex items-center"
            style={{ color: colors.primary }}
            onClick={startAddGoal}
          >
            <Plus className="h-5 w-5 mr-1" /> New Goal
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">Set and track your academic and career goals</p>
        
        {/* Goal Type Tabs */}
        <div className="flex space-x-2 mb-4">
          <button
            className={`px-4 py-2 rounded-full transition-colors ${
              goalType === "short-term"
                ? "text-white"
                : "text-gray-600"
            }`}
            style={{
              backgroundColor: goalType === "short-term" ? colors.primary : colors.lightBg
            }}
            onClick={() => toggleGoalType("short-term")}
          >
            <div className="flex items-center">
              <Target className="h-4 w-4 mr-2" />
              Short-term Goals
            </div>
          </button>
          <button
            className={`px-4 py-2 rounded-full transition-colors ${
              goalType === "long-term"
                ? "text-white"
                : "text-gray-600"
            }`}
            style={{
              backgroundColor: goalType === "long-term" ? colors.primary : colors.lightBg
            }}
            onClick={() => toggleGoalType("long-term")}
          >
            <div className="flex items-center">
              <Flag className="h-4 w-4 mr-2" />
              Long-term Goals
            </div>
          </button>
        </div>
        
        {/* New Goal Form */}
        {newGoal && (
          <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: getShadeBackground('25') }}>
            <h4 className="font-medium mb-2" style={{ color: colors.text }}>New {goalType === "short-term" ? "Short-term" : "Long-term"} Goal</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Goal Title</label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  className="w-full bg-white border rounded p-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="What do you want to achieve?"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Target Date</label>
                <input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                  className="w-full bg-white border rounded p-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={cancelAddGoal}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveNewGoal}
                className="px-3 py-1 text-white rounded"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                disabled={!newGoal.title.trim()}
              >
                Save Goal
              </button>
            </div>
          </div>
        )}
        
        {/* Goal List */}
        <div className="space-y-4">
          {goalType === "short-term" ? (
            progressData.shortTermGoals && progressData.shortTermGoals.length > 0 ? (
              progressData.shortTermGoals.map(goal => (
                <div key={goal.id} className={`p-4 rounded-lg ${goal.isCompleted ? 'border-l-4 border-green-500' : ''}`} style={{ backgroundColor: getShadeBackground('25') }}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-start">
                      <button 
                        className="mt-1 mr-3 text-gray-500"
                        style={{ color: colors.primary }}
                        onClick={() => toggleGoalCompletion(goal.id)}
                      >
                        {goal.isCompleted ? 
                          <CheckSquare className="h-5 w-5" /> : 
                          <Square className="h-5 w-5" />
                        }
                      </button>
                      <div>
                        <h4 className={`font-medium ${goal.isCompleted ? 'line-through text-gray-400' : ''}`} style={{ color: goal.isCompleted ? '#9CA3AF' : colors.text }}>
                          {goal.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">Target: {goal.deadline}</p>
                        
                        {/* Progress bar */}
                        {goal.steps && goal.steps.length > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Progress</span>
                              <span>{calculateGoalProgress(goal)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full" 
                                style={{ 
                                  width: `${calculateGoalProgress(goal)}%`,
                                  backgroundColor: colors.primary
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                        
                        {/* Steps list */}
                        {goal.steps && goal.steps.length > 0 && (
                          <div className="mt-3 ml-1">
                            <h5 className="text-sm text-gray-600 mb-1">Steps:</h5>
                            <ul className="space-y-1">
                              {goal.steps.map(step => (
                                <li key={step.id} className="flex items-start">
                                  <button 
                                    className="mt-0.5 mr-2 text-gray-500"
                                    style={{ color: colors.primary }}
                                    onClick={() => toggleStepCompletion(goal.id, step.id)}
                                  >
                                    {step.isCompleted ? 
                                      <CheckSquare className="h-4 w-4" /> : 
                                      <Square className="h-4 w-4" />
                                    }
                                  </button>
                                  <span className={`text-sm ${step.isCompleted ? 'line-through text-gray-500' : 'text-gray-600'}`}>
                                    {step.title}
                                  </span>
                                  <button 
                                    className="ml-2 text-gray-500 hover:text-red-600"
                                    onClick={() => deleteStep(goal.id, step.id)}
                                  >
                                    <XCircle className="h-3 w-3" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Add step form */}
                        {addingStep === goal.id ? (
                          <div className="mt-2 ml-1">
                            <div className="flex items-center">
                              <input
                                type="text"
                                className="flex-1 bg-white border rounded p-1 text-sm"
                                style={{ 
                                  borderColor: colors.accent,
                                  color: colors.text
                                }}
                                placeholder="Enter step description"
                                id={`new-step-${goal.id}`}
                                autoFocus
                              />
                              <button 
                                className="ml-2"
                                style={{ color: colors.primary }}
                                onClick={() => saveNewStep(goal.id, document.getElementById(`new-step-${goal.id}`).value)}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <button 
                                className="ml-1 text-gray-500 hover:text-gray-700"
                                onClick={() => setAddingStep(null)}
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button 
                            className="mt-2 text-sm flex items-center"
                            style={{ color: colors.primary }}
                            onClick={() => addStepToGoal(goal.id)}
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add step
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      <button 
                        className="text-gray-500 ml-2"
                        style={{ color: colors.primary }}
                        onClick={() => handleEditGoal(goal)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-gray-500 ml-2"
                        style={{ color: colors.primary }}
                        onClick={() => deleteGoal(goal.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Goal className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No short-term goals added yet</p>
                <button
                  className="mt-2"
                  style={{ color: colors.primary }}
                  onClick={startAddGoal}
                >
                  + Add your first short-term goal
                </button>
              </div>
            )
          ) : (
            progressData.longTermGoals && progressData.longTermGoals.length > 0 ? (
              progressData.longTermGoals.map(goal => (
                <div key={goal.id} className={`p-4 rounded-lg ${goal.isCompleted ? 'border-l-4 border-green-500' : ''}`} style={{ backgroundColor: getShadeBackground('25') }}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-start">
                      <button 
                        className="mt-1 mr-3 text-gray-500"
                        style={{ color: colors.primary }}
                        onClick={() => toggleGoalCompletion(goal.id)}
                      >
                        {goal.isCompleted ? 
                          <CheckSquare className="h-5 w-5" /> : 
                          <Square className="h-5 w-5" />
                        }
                      </button>
                      <div>
                        <h4 className={`font-medium ${goal.isCompleted ? 'line-through text-gray-400' : ''}`} style={{ color: goal.isCompleted ? '#9CA3AF' : colors.text }}>
                          {goal.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">Target: {goal.deadline}</p>
                        
                        {/* Progress bar */}
                        {goal.steps && goal.steps.length > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Progress</span>
                              <span>{calculateGoalProgress(goal)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full" 
                                style={{ 
                                  width: `${calculateGoalProgress(goal)}%`,
                                  backgroundColor: colors.primary
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                        
                        {/* Steps list */}
                        {goal.steps && goal.steps.length > 0 && (
                          <div className="mt-3 ml-1">
                            <h5 className="text-sm text-gray-600 mb-1">Steps:</h5>
                            <ul className="space-y-1">
                              {goal.steps.map(step => (
                                <li key={step.id} className="flex items-start">
                                  <button 
                                    className="mt-0.5 mr-2 text-gray-500"
                                    style={{ color: colors.primary }}
                                    onClick={() => toggleStepCompletion(goal.id, step.id)}
                                  >
                                    {step.isCompleted ? 
                                      <CheckSquare className="h-4 w-4" /> : 
                                      <Square className="h-4 w-4" />
                                    }
                                  </button>
                                  <span className={`text-sm ${step.isCompleted ? 'line-through text-gray-500' : 'text-gray-600'}`}>
                                    {step.title}
                                  </span>
                                  <button 
                                    className="ml-2 text-gray-500 hover:text-red-600"
                                    onClick={() => deleteStep(goal.id, step.id)}
                                  >
                                    <XCircle className="h-3 w-3" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Add step form */}
                        {addingStep === goal.id ? (
                          <div className="mt-2 ml-1">
                            <div className="flex items-center">
                              <input
                                type="text"
                                className="flex-1 bg-white border rounded p-1 text-sm"
                                style={{ 
                                  borderColor: colors.accent,
                                  color: colors.text
                                }}
                                placeholder="Enter step description"
                                id={`new-step-${goal.id}`}
                                autoFocus
                              />
                              <button 
                                className="ml-2"
                                style={{ color: colors.primary }}
                                onClick={() => saveNewStep(goal.id, document.getElementById(`new-step-${goal.id}`).value)}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <button 
                                className="ml-1 text-gray-500 hover:text-gray-700"
                                onClick={() => setAddingStep(null)}
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button 
                            className="mt-2 text-sm flex items-center"
                            style={{ color: colors.primary }}
                            onClick={() => addStepToGoal(goal.id)}
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add step
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      <button 
                        className="text-gray-500 ml-2"
                        style={{ color: colors.primary }}
                        onClick={() => handleEditGoal(goal)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-gray-500 ml-2"
                        style={{ color: colors.primary }}
                        onClick={() => deleteGoal(goal.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Flag className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No long-term goals added yet</p>
                <button
                  className="mt-2"
                  style={{ color: colors.primary }}
                  onClick={startAddGoal}
                >
                  + Add your first long-term goal
                </button>
              </div>
            )
          )}
        </div>
      </div>
      {/* Goal Editing Modal */}
      {editingGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>Edit Goal</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Goal Title</label>
                <input
                  type="text"
                  value={editingGoal.title}
                  onChange={(e) => setEditingGoal({...editingGoal, title: e.target.value})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Target Date</label>
                <input
                  type="date"
                  value={editingGoal.deadline.split('/').reverse().join('-')}
                  onChange={(e) => setEditingGoal({...editingGoal, deadline: e.target.value.split('-').reverse().join('/')})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="goalCompleted"
                    checked={editingGoal.isCompleted}
                    onChange={(e) => setEditingGoal({...editingGoal, isCompleted: e.target.checked})}
                    className="rounded"
                    style={{ 
                      backgroundColor: colors.lightBg,
                      borderColor: colors.accent,
                      accentColor: colors.primary
                    }}
                  />
                  <label htmlFor="goalCompleted" className="text-gray-600">Mark as completed</label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={cancelEditGoal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveGoalChanges}
                className="px-4 py-2 text-white rounded"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                disabled={!editingGoal.title.trim()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Education Progress Board */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xl font-semibold" style={{ color: colors.text }}>Education Progress Board</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Track your educational tasks and progress</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* To Do Column */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium" style={{ color: colors.text }}>To Do</h4>
              <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: colors.lightBg, color: colors.text }}>{getTasksByStatus('todo').length}</span>
            </div>
            
            {getTasksByStatus('todo').map(task => (
              <div key={task.id} className="p-3 rounded-lg mb-2 relative" style={{ backgroundColor: getShadeBackground('20') }}>
                {showTaskMenu === task.id && (
                  <div className="absolute right-0 top-8 z-10 bg-white border rounded shadow-lg py-1 min-w-32" style={{ borderColor: colors.accent }}>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => handleEditTask(task)}
                    >
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'inprogress')}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" /> Mark In Progress
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'completed')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" /> Mark Complete
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left text-red-600 hover:bg-gray-50"
                      onClick={() => deleteTask(task.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </button>
                  </div>
                )}
                
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-medium" style={{ color: colors.text }}>{task.title}</h5>
                    <p className="text-xs mt-1 text-gray-600">Due: {task.dueDate}</p>
                  </div>
                  <button 
                    className="text-gray-500"
                    style={{ color: colors.primary }}
                    onClick={() => toggleTaskMenu(task.id)}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {newTask && newTask.status === 'todo' ? (
              <div className="p-3 rounded-lg mb-2" style={{ backgroundColor: getShadeBackground('20') }}>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="Task title"
                  autoFocus
                />
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={cancelAddTask}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNewTask}
                    className="px-3 py-1 text-white rounded"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                    disabled={!newTask.title.trim()}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="w-full mt-2 flex items-center justify-center text-sm"
                style={{ color: colors.primary }}
                onClick={() => startAddTask('todo')}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Task
              </button>
            )}
          </div>
          
          {/* In Progress Column */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium" style={{ color: colors.text }}>In Progress</h4>
              <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: colors.lightBg, color: colors.text }}>{getTasksByStatus('inprogress').length}</span>
            </div>
            
            {getTasksByStatus('inprogress').map(task => (
              <div key={task.id} className="p-3 rounded-lg mb-2 relative" style={{ backgroundColor: getShadeBackground('20') }}>
                {showTaskMenu === task.id && (
                  <div className="absolute right-0 top-8 z-10 bg-white border rounded shadow-lg py-1 min-w-32" style={{ borderColor: colors.accent }}>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => handleEditTask(task)}
                    >
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'todo')}
                    >
                      <XCircle className="h-4 w-4 mr-2" /> Move to To Do
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'completed')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" /> Mark Complete
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left text-red-600 hover:bg-gray-50"
                      onClick={() => deleteTask(task.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </button>
                  </div>
                )}
                
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-medium" style={{ color: colors.text }}>{task.title}</h5>
                    <p className="text-xs mt-1 text-gray-600">Due: {task.dueDate}</p>
                  </div>
                  <button 
                    className="text-gray-500"
                    style={{ color: colors.primary }}
                    onClick={() => toggleTaskMenu(task.id)}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {newTask && newTask.status === 'inprogress' ? (
              <div className="p-3 rounded-lg mb-2" style={{ backgroundColor: getShadeBackground('20') }}>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="Task title"
                  autoFocus
                />
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={cancelAddTask}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNewTask}
                    className="px-3 py-1 text-white rounded"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                    disabled={!newTask.title.trim()}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="w-full mt-2 flex items-center justify-center text-sm"
                style={{ color: colors.primary }}
                onClick={() => startAddTask('inprogress')}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Task
              </button>
            )}
          </div>
          
          {/* Completed Column */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium" style={{ color: colors.text }}>Completed</h4>
              <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: colors.lightBg, color: colors.text }}>{getTasksByStatus('completed').length}</span>
            </div>
            
            {getTasksByStatus('completed').map(task => (
              <div key={task.id} className="p-3 rounded-lg mb-2 relative" style={{ backgroundColor: getShadeBackground('20') }}>
                {showTaskMenu === task.id && (
                  <div className="absolute right-0 top-8 z-10 bg-white border rounded shadow-lg py-1 min-w-32" style={{ borderColor: colors.accent }}>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => handleEditTask(task)}
                    >
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'todo')}
                    >
                      <XCircle className="h-4 w-4 mr-2" /> Move to To Do
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                      style={{ color: colors.text }}
                      onClick={() => changeTaskStatus(task.id, 'inprogress')}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" /> Move to In Progress
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2 text-left text-red-600 hover:bg-gray-50"
                      onClick={() => deleteTask(task.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </button>
                  </div>
                )}
                
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-medium" style={{ color: colors.text }}>{task.title}</h5>
                    <p className="text-xs mt-1 text-gray-600">Due: {task.dueDate}</p>
                  </div>
                  <button 
                    className="text-gray-500"
                    style={{ color: colors.primary }}
                    onClick={() => toggleTaskMenu(task.id)}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {newTask && newTask.status === 'completed' ? (
              <div className="p-3 rounded-lg mb-2" style={{ backgroundColor: getShadeBackground('20') }}>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="Task title"
                  autoFocus
                />
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                  className="w-full bg-white border rounded p-2 mb-2"
                  style={{ 
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={cancelAddTask}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNewTask}
                    className="px-3 py-1 text-white rounded"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                    disabled={!newTask.title.trim()}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="w-full mt-2 flex items-center justify-center text-sm"
                style={{ color: colors.primary }}
                onClick={() => startAddTask('completed')}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Task
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Task Editing Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>Edit Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Due Date</label>
                <input
                  type="date"
                  value={editingTask.dueDate.split('/').reverse().join('-')}
                  onChange={(e) => setEditingTask({...editingTask, dueDate: e.target.value.split('-').reverse().join('/')})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={editingTask.status}
                  onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                >
                  <option value="todo">To Do</option>
                  <option value="inprogress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={cancelTaskEdit}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveTaskChanges}
                className="px-4 py-2 text-white rounded"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                disabled={!editingTask.title.trim()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Event Creation/Editing Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
              {newEvent.id ? 'Edit Event' : 'Add New Event'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Event Title</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="Enter event title"
                  autoFocus
                />
              </div>
              
              {/* New Event Type dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Event Type</label>
                <select
                  value={newEvent.eventType || 'other'}
                  onChange={(e) => setNewEvent({...newEvent, eventType: e.target.value})}
                  className="w-full border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                >
                  {eventTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
                
                {/* Color preview */}
                <div className="mt-1 flex items-center">
                  <span className="text-xs text-gray-500 mr-2">Color:</span>
                  <div 
                    className="w-6 h-3 rounded-sm inline-block"
                    style={{ backgroundColor: getEventColor(newEvent) }}
                  ></div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Description (optional)</label>
                <textarea
                  value={newEvent.description || ''}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="w-full border rounded p-2 h-20 resize-none"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                  placeholder="Add event details"
                ></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={newEvent.startTime || '09:00'}
                    onChange={(e) => setNewEvent({...newEvent, startTime: e.target.value})}
                    className="w-full border rounded p-2"
                    style={{ 
                      backgroundColor: colors.lightBg,
                      borderColor: colors.accent,
                      color: colors.text
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">End Time</label>
                  <input
                    type="time"
                    value={newEvent.endTime || '10:00'}
                    onChange={(e) => setNewEvent({...newEvent, endTime: e.target.value})}
                    className="w-full border rounded p-2"
                    style={{ 
                      backgroundColor: colors.lightBg,
                      borderColor: colors.accent,
                      color: colors.text
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Date</label>
                <div 
                  className="flex items-center border rounded p-2"
                  style={{ 
                    backgroundColor: colors.lightBg,
                    borderColor: colors.accent,
                    color: colors.text
                  }}
                >
                  {selectedDay && `${monthNames[selectedDay.month]} ${selectedDay.day}, ${selectedDay.year}`}
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <div>
                {newEvent.id && (
                  <button
                    onClick={() => handleDeleteEvent(newEvent.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={cancelAddEvent}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={saveNewEvent}
                  className="px-4 py-2 text-white rounded"
                  style={{ backgroundColor: colors.primary }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent}
                  onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                  disabled={!newEvent.title.trim()}
                >
                  {newEvent.id ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AppLayout activeTab="progress">
      <div className="mb-4 p-4 bg-white rounded-lg shadow-sm text-[#4a1825]" role="note">
        <p className="font-medium">Saved only in this browser — not synchronized between devices.</p>
        <p>{guest ? 'Guest progress is temporary and tied to this guest session. It is not transferred when you register or sign in.' : 'Notes, tasks, goals, deadlines, and calendar entries are separated by your signed-in account.'}</p>
        <p>Course, clinical-hour, and research totals are not connected yet. Legacy shared browser data is left untouched and is not imported.</p>
      </div>
      {localProgress.error && <div role="alert" className="mb-4 p-4 bg-red-50 text-red-800 rounded-lg">
        <p>{localProgress.error}</p>
        <button type="button" onClick={localProgress.retry} className="mt-2 underline">Retry loading saved progress</button>
      </div>}
      <fieldset disabled={localProgress.readFailed} className="min-w-0">{renderContent()}</fieldset>
    </AppLayout>
  );
};

// Helper component for dropdown
const ChevronDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

export default function Progress(props) {
  const { user } = useFirebase();
  return user ? <CareerProgress key={user.uid} {...props} uid={user.uid} guest={user.isAnonymous} /> : null;
}
